/**
 * Phase 41 (Plan 41-15 Task 2) — Sidebar v3 Customer to Project hierarchy
 * block.
 *
 * D-Sidebar-v3 contract:
 * - One collapsible row per customer (chevron + name + footer count chip).
 * - Each project row: truncated name with full-text hover tooltip
 *   (`title={project.name}`) + Billable / Internal badge.
 * - Click on a project row navigates to `/projects/{id}/board` via React
 *   Router Link.
 * - Counts on the customer row footer: "Billable . N" if all projects
 *   billable; "Billable: X . Internal: Y" if mixed.
 *
 * Per-customer expand state lives in component-local state so the bar
 * can be remounted (route change) without losing the user's expansion
 * choices for the current session. Persistence across mounts is out of
 * scope for 41-15 (deferred to v7.1 if needed).
 *
 * MKS-542 — behind `mks_542_sidebar_group_rows` the rows stop competing
 * for the 240px sidebar's width. The one-line customer row gave its name
 * whatever the count chip left over ("To... BILLABLE: 1 · INTERNAL: 3"),
 * and every project row gave its name whatever the Billable / Internal
 * badge left over ("AI Scraping (Scra..."). With the flag on the customer
 * row stacks name over counts, and the badge leaves the project rows: a
 * mixed customer labels its two groups instead, so the name owns the full
 * row. No information is dropped — it is only moved off the name's line.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useFlag } from '../../services/flagsContext';
import type { SidebarCustomer, SidebarProject } from '../../services/api/sidebar';

interface SidebarProjectsSectionProps {
  customers: SidebarCustomer[];
  isLoading?: boolean;
  isError?: boolean;
  /**
   * Quick task 260512-uat-batch (F-07, 2026-05-12) — Petra wants role-
   * conditional project click destinations:
   *   - admin / PM → /projects/{id} (detail page with "Open board" CTA)
   *   - plain user (member only) → /projects/{id}/board (the working
   *     surface; detail page is gated to manager+ anyway)
   * `linkTarget="detail" | "board"` controls which URL the rows emit.
   * Caller (Sidebar) derives this from auth flags.
   */
  linkTarget?: 'detail' | 'board';
}

function customerFooterChip(customer: SidebarCustomer): string {
  const billable = customer.projects.filter((p) => p.billable).length;
  const internal = customer.projects.length - billable;
  if (internal === 0) return `Billable · ${billable}`;
  if (billable === 0) return `Internal · ${internal}`;
  return `Billable: ${billable} · Internal: ${internal}`;
}

/**
 * MKS-542 — the project rows of one customer, split into the groups the
 * per-row badge used to carry. A customer whose projects are all of one
 * kind gets a single unlabelled group: its customer row chip already says
 * "Billable · 3", so a lone "BILLABLE" header above the rows would only
 * repeat it. A mixed customer gets both groups, each with its own header,
 * which is where "which of these is internal?" now lives.
 */
function projectGroups(
  customer: SidebarCustomer
): { key: string; label: string | null; projects: SidebarProject[] }[] {
  const billable = customer.projects.filter((p) => p.billable);
  const internal = customer.projects.filter((p) => !p.billable);
  if (billable.length === 0 || internal.length === 0) {
    return [{ key: 'all', label: null, projects: customer.projects }];
  }
  return [
    { key: 'billable', label: `Billable · ${billable.length}`, projects: billable },
    { key: 'internal', label: `Internal · ${internal.length}`, projects: internal },
  ];
}

export function SidebarProjectsSection({
  customers,
  isLoading,
  isError,
  linkTarget = 'board',
}: SidebarProjectsSectionProps) {
  // Per-customer expansion state — open by default for the first customer
  // so the user immediately sees a project row and understands the
  // hierarchy. Subsequent customers start collapsed.
  const [expanded, setExpanded] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {};
    if (customers.length > 0) initial[customers[0]!.id] = true;
    return initial;
  });
  // MKS-542 — stacked customer row + grouped, badge-free project rows.
  const stackedRows = useFlag('mks_542_sidebar_group_rows');

  const projectHref = (project: SidebarProject) =>
    linkTarget === 'detail' ? `/projects/${project.id}` : `/projects/${project.id}/board`;

  if (isLoading) {
    return <div className="px-2 py-1.5 text-[11.5px] text-ms-text-muted">Loading projects...</div>;
  }

  if (isError) {
    return <div className="px-2 py-1.5 text-[11.5px] text-rose-500">Failed to load projects</div>;
  }

  if (customers.length === 0) {
    return (
      <div className="px-2 py-1.5 text-[11.5px] text-ms-text-muted italic">
        You are not a member of any active project yet.
      </div>
    );
  }

  return (
    <div data-testid="sidebar-projects-section">
      {customers.map((customer) => {
        const isOpen = !!expanded[customer.id];
        const toggle = () =>
          setExpanded((prev) => ({ ...prev, [customer.id]: !prev[customer.id] }));
        return (
          <div key={customer.id} className="mb-1">
            {stackedRows ? (
              <button
                type="button"
                onClick={toggle}
                className="flex flex-col gap-0.5 px-2 py-1.5 rounded-md text-[12px] font-medium w-full text-left text-ms-text-secondary hover:bg-ms-surface-2"
                aria-expanded={isOpen}
              >
                <span className="flex items-start gap-2 w-full">
                  <ChevronRightIcon
                    className={`w-3.5 h-3.5 mt-0.5 shrink-0 transition-transform ${
                      isOpen ? 'rotate-90' : ''
                    }`}
                  />
                  <span className="flex-1 min-w-0 break-words" title={customer.name}>
                    {customer.name}
                  </span>
                </span>
                {/* 22px = the chevron (14px) plus the gap (8px), so the counts
                    line up under the name rather than under the chevron. */}
                <span className="pl-[22px] text-[10px] uppercase tracking-wider text-ms-text-muted">
                  {customerFooterChip(customer)}
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={toggle}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] font-medium w-full text-left text-ms-text-secondary hover:bg-ms-surface-2"
                aria-expanded={isOpen}
              >
                <ChevronRightIcon
                  className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
                <span className="flex-1 truncate" title={customer.name}>
                  {customer.name}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-ms-text-muted shrink-0">
                  {customerFooterChip(customer)}
                </span>
              </button>
            )}
            {isOpen &&
              (stackedRows ? (
                <div className="ml-5 mt-0.5">
                  {projectGroups(customer).map((group) => (
                    <div key={group.key}>
                      {group.label !== null && (
                        <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-ms-text-muted">
                          {group.label}
                        </div>
                      )}
                      {group.projects.map((project) => (
                        <Link
                          key={project.id}
                          to={projectHref(project)}
                          className="block px-2 py-1.5 rounded-md text-[12px] break-words text-ms-text-secondary hover:bg-ms-surface-2"
                          title={project.name}
                        >
                          {project.name}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ml-5 mt-0.5">
                  {customer.projects.map((project) => (
                    <Link
                      key={project.id}
                      to={projectHref(project)}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-ms-text-secondary hover:bg-ms-surface-2"
                    >
                      <span
                        className="flex-1 text-ellipsis overflow-hidden whitespace-nowrap"
                        title={project.name}
                      >
                        {project.name}
                      </span>
                      <span
                        className={`text-[10px] uppercase tracking-wider shrink-0 px-1.5 py-0.5 rounded ${
                          project.billable
                            ? 'bg-emerald-500/10 text-emerald-600'
                            : 'bg-sky-500/10 text-sky-600'
                        }`}
                      >
                        {project.billable ? 'Billable' : 'Internal'}
                      </span>
                    </Link>
                  ))}
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
}
